const isWeb = typeof document !== 'undefined' && typeof window !== 'undefined';

let PermissionsAndroid, Platform, RNHTMLtoPDF, RNFetchBlob, Alert;

if (!isWeb) {
  const RN = require('react-native');
  PermissionsAndroid = RN.PermissionsAndroid;
  Platform = RN.Platform;
  Alert = RN.Alert;
  RNHTMLtoPDF = require('react-native-html-to-pdf').default;
  RNFetchBlob = require('./custom_rn_fetch_blob').default;
}

// ─── Show Alert (cross-platform) ─────────────────────────────────────────────
const showAlert = (title, message) => {
  if (isWeb) {
    alert(`${title}: ${message}`);
  } else {
    Alert.alert(title, message, [{ text: 'OK' }]);
  }
};

// ─── Permission Request ───────────────────────────────────────────────────────
const requestDownloadPermission = async () => {
  if (isWeb) return 'granted';
  if (Platform.OS === 'ios') return 'granted';

  if (Platform.OS === 'android') {
    if (Platform.Version >= 34) {
      return 'granted'; // Android 14+ doesn't need WRITE_EXTERNAL_STORAGE
    }

    return await PermissionsAndroid.request(
      PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
      {
        title: 'Storage Permission',
        message: 'App needs storage access to save your receipt.',
        buttonPositive: 'Allow',
        buttonNegative: 'Cancel',
      }
    );
  }

  return 'denied';
};

// ─── Get Android Downloads Path ───────────────────────────────────────────────
const getDownloadPath = () => {
  const dirs = RNFetchBlob.fs.dirs;
  return dirs.DownloadDir.includes('Downloads') ? 'Downloads' : 'Download';
};

// ─── WEB: Render HTML into hidden iframe then capture + export as PDF ─────────
const downloadPDFOnWeb = async (htmlContent, fileName) => {
  try {
    // Dynamically import to avoid issues in React Native bundler
    const jsPDFModule = await import('jspdf');
    const html2canvasModule = await import('html2canvas');
    const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
    const html2canvas = html2canvasModule.default;

    // ── Step 1: Create hidden iframe to render the HTML ───────────────────
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '794px';   // A4 width at 96dpi
    iframe.style.height = '1123px'; // A4 height at 96dpi
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    // ── Step 2: Write HTML into iframe ────────────────────────────────────
    await new Promise((resolve) => {
      iframe.onload = resolve;
      iframe.contentDocument.open();
      iframe.contentDocument.write(htmlContent);
      iframe.contentDocument.close();
    });

    // ── Step 3: Wait for fonts/images to load ─────────────────────────────
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // ── Step 4: Capture iframe content with html2canvas ───────────────────
    const canvas = await html2canvas(iframe.contentDocument.body, {
      scale: 1.5,           // Higher resolution
      useCORS: true,      // Allow cross-origin images (base64 logos work fine)
      allowTaint: true,
      backgroundColor: '#FFFFFF',
      width: 794,
      windowWidth: 794,
    });

    // ── Step 5: Cleanup iframe ────────────────────────────────────────────
    document.body.removeChild(iframe);

    // ── Step 6: Convert canvas to PDF ─────────────────────────────────────
    const imgData = canvas.toDataURL('image/jpeg', 0.85);
    const imgWidth = 210;   // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    let heightLeft = imgHeight;
    let position = 0;

    // ── Step 7: Add pages if content overflows ────────────────────────────
    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // ── Step 8: Trigger download ───────────────────────────────────────────
    pdf.save(`${fileName}.pdf`);

    return { success: true, platform: 'web', fileName: `${fileName}.pdf` };

  } catch (err) {
    console.error('PDF generation failed:', err);
    showAlert('Error', `Failed to generate PDF: ${err}`);
    return { success: false, reason: err };
  }
};

// ─── MOBILE: Download as PDF ──────────────────────────────────────────────────
const downloadPDFOnMobile = async (html, fileName) => {
  const permission = await requestDownloadPermission();
  if (permission !== 'granted') {
    showAlert(
      'Permission Denied',
      'Storage permission is required to save the receipt.'
    );
    return { success: false, reason: 'permission_denied' };
  }

  try {
    const options = {
      html,
      fileName,
      directory: Platform.OS === 'ios'
        ? 'Documents'
        : getDownloadPath(),
      height: 900,
      base64: true,
    };

    const file = await RNHTMLtoPDF.convert(options);

    if (Platform.OS === 'ios') {
      showAlert('Success', 'Receipt saved in Documents folder.');
      return { success: true, platform: 'ios', path: 'Documents' };

    } else {
      const filePath =
        `${RNFetchBlob.fs.dirs.DownloadDir}/${fileName}.pdf`;

      await RNFetchBlob.fs.writeFile(filePath, file.base64, 'base64');
      showAlert('Success', `Receipt saved to:\n${filePath}`);
      return { success: true, platform: 'android', path: filePath };
    }

  } catch (err) {
    showAlert('Error', `Failed to save receipt: ${err}`);
    return { success: false, reason: err };
  }
};

// ─── Main Entry Point ─────────────────────────────────────────────────────────
export const downloadReceipt = async ({ html, fileName }) => {
  try {
    if (!html) {
      showAlert('Error', 'No content provided for download.');
      return { success: false, reason: 'no_html' };
    }

    if (!fileName) {
      showAlert('Error', 'No file name provided.');
      return { success: false, reason: 'no_filename' };
    }

    // ── WEB: HTML → Canvas → PDF download ─────────────────────────────────
    if (isWeb) {
      return await downloadPDFOnWeb(html, fileName);
    }

    // ── ANDROID / iOS: HTML → PDF via RNHTMLtoPDF ─────────────────────────
    return await downloadPDFOnMobile(html, fileName);

  } catch (err) {
    showAlert('Error', `Something went wrong: ${err}`);
    return { success: false, reason: err };
  }
};

export default {
  downloadReceipt,
  requestDownloadPermission,
  isWeb,
};